import { useQuery } from '@tanstack/react-query'
import { debounce, isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CgSmileNone } from 'react-icons/cg'
import { FaFilterCircleXmark } from 'react-icons/fa6'
import { ImSpinner } from 'react-icons/im'
import { PiMicrosoftExcelLogoFill } from 'react-icons/pi'
import { useInView } from 'react-intersection-observer'

import { Button } from '../../components/Button'
import { FloatingHeader } from '../../components/FloatingHeader'
import { FloatingPromotersStayingTimeCounter } from '../../components/FloatingPromotersStayingTimeCounter'
import { ModalAddImages } from '../../components/ModalAddImages'
import { ReportCard } from '../../components/ReportCard'
import { usePermission } from '../../hooks/usePermission'
import { useReports } from '../../hooks/useReports'
import { getReportsWithoutPagination, Report } from '../../services/reportServices'
import { getAllStoreGroupsByUser, getAllStores, getGroupCities, getGroupStates } from '../../services/storesServices'
import { exportToExcel } from '../../utils/exportToExcel'
import { normalizeString } from '../../utils/normalizeString'
import { CitiesDashboardSelect } from './components/CitiesDashboardSelect'
import { GroupsDashboardButtons } from './components/GroupsDashboardButtons'
import { StatesDashboardButtons } from './components/StatesDashboardButtons'
import { StoreUnityDashboardSelect } from './components/StoreUnityDashboardSelect'

export function Dashboard() {
  const [filteredStates, setFilteredStates] = useState<{ id: string, name: string }[]>([])
  const [filteredCities, setFilteredCities] = useState<{ id: string, name: string }[]>([])
  const [filteredStores, setFilteredStores] = useState<{ id: string, name: string, state: string, city: string, group: string }[]>([])

  const [selectedGroup, setSelectedGroup] = useState<string | undefined>()
  const [selectedState, setSelectedState] = useState<string | undefined>()
  const [selectedCity, setSelectedCity] = useState<string | undefined>()
  const [selectedStore, setSelectedStore] = useState<string | undefined>()

  const [selectedReport, setSelectedReport] = useState<Report | null>(null)

  const [isAddImagesModalOpen, setIsAddImagesModalOpen] = useState(false)

  const { canExportReports } = usePermission()

  const { ref: refInView, inView } = useInView({
    threshold: 0.8,
  })

  const stableFilters = useMemo(() => ({
    group_id: selectedGroup,
    state: selectedState,
    city: selectedCity,
    store_id: selectedStore,
  }), [selectedGroup, selectedState, selectedCity, selectedStore])

  const { data: result, isLoading, fetchNextPage, hasNextPage, refetch } = useReports(stableFilters, 'dashboard_reports')

  const reports = result?.pages.flatMap((page) => page.reports) || []

  function handleClickAddImages(report: Report) {
    setSelectedReport(report)
    setIsAddImagesModalOpen(true)
  }

  function handleCloseAddImagesModal() {
    setSelectedReport(null)
    setIsAddImagesModalOpen(false)
    refetch()
  }

  function handleSelectGroup(value: string) {
    setSelectedGroup(value)
  }

  function handleSelectState(value: string) {
    setSelectedState(value)
  }

  function handleSelectCity(value: string) {
    setSelectedCity(value)
  }

  function handleSelectStore(value: string) {
    setSelectedStore(value)
  }

  const { data: groups } = useQuery({
    queryKey: ['dash_groups'],
    queryFn: async () => {
      return await getAllStoreGroupsByUser()
    },
  })

  useQuery({
    queryKey: ['dash_states', selectedGroup],
    queryFn: async () => {
      const statesList = await getGroupStates(selectedGroup)
      setFilteredStates(statesList.map(item => ({ id: item, name: item })))
      return statesList
    },
  })

  const { data: cities } = useQuery({
    queryKey: ['dash_cities', selectedGroup, selectedState],
    queryFn: async () => {
      const filters = {
        group: selectedGroup,
        state: selectedState,
      }
      const searchList = Object.entries(filters)
      const citiesList = (await getGroupCities(selectedGroup)).filter(item => {
        return searchList.every(([key, value]) => {
          if (value === undefined) return true
          if (key === 'group') {
            return item[key].id === value
          } else if (key === 'state') {
            return item[key] === value
          }
          return false
        })
      })
      setFilteredCities(citiesList.map(item => ({ id: item.city, name: item.city })))
      return citiesList
    },
  })

  const { data: stores } = useQuery({
    queryKey: ['dash_stores', selectedCity, selectedState, selectedGroup],
    queryFn: async () => {
      const filters = {
        group: selectedGroup,
        state: selectedState,
        city: selectedCity,
      }
      const searchList = Object.entries(filters)
      const storesList = (await getAllStores()).stores
        .filter(item => {
          return searchList.every(([key, value]) => {
            if (value === undefined) return true
            if (key === 'group') {
              return item[key].id === value
            } else if (key === 'state' || key === 'city') {
              return item[key] === value
            }
            return false
          })
        })
        .map(item => ({
          id: item.id,
          name: item.name,
          state: item.state,
          city: item.city,
          group: item.group.name,
        }))
      setFilteredStores(storesList)
      return storesList
    },
  })

  const normalizedCities = useMemo(() => {
    return cities?.map(item => ({
      ...item,
      normalizedCity: normalizeString(item.city),
    })) || []
  }, [cities])

  const debouncedSearchCities = useMemo(
    () =>
      debounce((search: string) => {
        if (normalizedCities.length) {
          let newList
          if (!search) {
            newList = normalizedCities.map(item => ({ id: item.city, name: item.city }))
          } else {
            const normSearch = normalizeString(search)
            newList = normalizedCities
              .filter(item => item.normalizedCity.includes(normSearch))
              .map(item => ({ id: item.city, name: item.city }))
          }
          setFilteredCities(prevList => {
            if (isEqual(prevList, newList)) {
              return prevList
            }
            return newList
          })
        }
      }, 650),
    [normalizedCities],
  )

  const handleTypeFilterCities = useCallback(
    (value: string) => {
      debouncedSearchCities(value)
    },
    [debouncedSearchCities],
  )

  const normalizedStores = useMemo(() => {
    return stores?.map(item => ({
      ...item,
      normalizedName: normalizeString(item.name),
    })) || []
  }, [stores])

  const debouncedSearchStores = useMemo(
    () =>
      debounce((search: string) => {
        if (normalizedStores.length) {
          let newList
          if (!search) {
            newList = stores ?? []
          } else {
            const normSearch = normalizeString(search)
            newList = normalizedStores.filter(item =>
              item.normalizedName.includes(normSearch),
            )
          }
          setFilteredStores((prevList) => {
            if (isEqual(prevList, newList)) {
              return prevList
            }
            return newList
          })
        }
      }, 650),
    [normalizedStores, stores],
  )

  const handleTypeFilterStores = useCallback(
    (value: string) => {
      debouncedSearchStores(value)
    },
    [debouncedSearchStores],
  )

  useEffect(() => {
    return () => {
      debouncedSearchCities.cancel()
      debouncedSearchStores.cancel()
    }
  }, [debouncedSearchCities, debouncedSearchStores])

  function handleClearFilters() {
    setSelectedGroup(undefined)
    setSelectedState(undefined)
    setSelectedCity(undefined)
    setSelectedStore(undefined)
  }

  async function handleExportToExcel() {
    const result = await getReportsWithoutPagination(stableFilters)
    if (result.reports.length > 0) {
      await exportToExcel(result.reports, `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`)
    }
  }

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  return (
    <div className="min-h-screen">
      <FloatingHeader />
      <FloatingPromotersStayingTimeCounter />
      <div className="flex flex-col mx-auto max-w-screen-lg">
        <div className="w-full space-y-4 overflow-hidden mt-6">
          {groups && groups.storeGroups.length > 0
            ? (
              <>
                <GroupsDashboardButtons
                  storeGroups={groups
                    ? groups.storeGroups
                    : []}
                  onSelectGroup={handleSelectGroup}
                  selectedGroup={selectedGroup}
                />
                <StatesDashboardButtons
                  states={filteredStates}
                  onSelectState={handleSelectState}
                  selectedState={selectedState}
                />
                <div className="space-y-1">
                  <CitiesDashboardSelect
                    handleTypeFilterCities={handleTypeFilterCities}
                    onSelectCity={handleSelectCity}
                    cities={filteredCities}
                    selectedCity={selectedCity}
                  />
                  <StoreUnityDashboardSelect
                    handleTypeFilterStores={handleTypeFilterStores}
                    onSelectStore={handleSelectStore}
                    stores={filteredStores}
                    selectedStore={selectedStore}
                  />
                </div>
              </>
              )
            : (
              <>
                {!isLoading && (
                  <div className="flex flex-col items-center justify-center border rounded-md h-[50dvh] m-8 p-8">
                    <strong className="text-gray-700 font-semibold text-center">
                      Não á nenhuma loja vinculada ao seu usuário
                    </strong>
                    <CgSmileNone className="text-gray-700" size={28} />
                  </div>
                )}
              </>
              )}
        </div>
        <footer className="flex gap-2 md:justify-end px-4 pt-4">
          {canExportReports && (
            <Button
              className="w-14 h-14 rounded-full"
              type="button"
              onClick={() => handleExportToExcel()}
            >
              <PiMicrosoftExcelLogoFill />
            </Button>
          )}
          {
            (selectedGroup || selectedState || selectedCity || selectedStore) &&
              <Button
                className="w-14 h-14 rounded-full shadow-lg border-2 border-indigo-200 fixed right-4 bottom-4 md:border-0 md:static z-[9999]"
                type="button"
                onClick={() => handleClearFilters()}
              >
                <FaFilterCircleXmark />
              </Button>
          }
        </footer>
        <main className="space-y-4 mt-8">
          {
            reports.map(report => (
              <ReportCard key={report.id} report={report} onAddImagesClick={handleClickAddImages} />
            ))
          }
          {isLoading &&
            <div className="flex w-full items-center justify-center text-slate-400 pt-8">
              <ImSpinner className="animate-spin w-10 h-10" />
            </div>}
          <div ref={refInView} className="h-5" />
        </main>
      </div>
      {isAddImagesModalOpen &&
        <ModalAddImages
          report={selectedReport}
          onClose={handleCloseAddImagesModal}
          filters={stableFilters}
        />}
    </div>
  )
}
